// src/admin/screens/login/AcceptInvite.tsx
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@admin/lib/supabase';
import type { Database } from '@/lib/database.types';
import styles from './Login.module.css';

const schema = z.object({
  display_name: z.string().min(1, 'Required'),
  password: z.string().min(12, 'At least 12 characters'),
});
type FormValues = z.infer<typeof schema>;

type InvitePreview =
  Database['public']['Functions']['admin_invite_claim_preview']['Returns'];

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { register, handleSubmit, formState: { isSubmitting, errors } } =
    useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    void (async () => {
      if (!token) { setErr('Missing invite token.'); return; }
      const { data, error } = await supabase.rpc('admin_invite_claim_preview', { p_token: token });
      if (error) { setErr(error.message); return; }
      setInvite(data);
    })();
  }, [token]);

  const onSubmit = async ({ display_name, password }: FormValues) => {
    if (!invite || !token) return;
    setErr(null);
    const { error: rpcError } = await supabase.rpc('admin_invite_claim', {
      p_token: token,
      p_display_name: display_name,
      p_password: password,
    });
    if (rpcError) { setErr(rpcError.message); return; }
    const signIn = await supabase.auth.signInWithPassword({ email: invite.email, password });
    if (signIn.error) { setErr(signIn.error.message); return; }
    navigate('/login/enrol-totp', { replace: true });
  };

  if (err) return <div className={styles.page}><div className={styles.card}><div className={styles.error}>{err}</div></div></div>;
  if (!invite) return <div className={styles.page}><div className={styles.card}>Loading invite…</div></div>;

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={handleSubmit(onSubmit)} noValidate>
        <h1>Welcome to Thandizo</h1>
        <p className="small">You've been invited as <strong>{invite.role}</strong>. Email: {invite.email}</p>
        <label>
          Display name
          <input autoFocus {...register('display_name')} />
          {errors.display_name && <span className={styles.fieldError}>{errors.display_name.message}</span>}
        </label>
        <label>
          Password (12+ chars)
          <input type="password" {...register('password')} />
          {errors.password && <span className={styles.fieldError}>{errors.password.message}</span>}
        </label>
        <button type="submit" className={styles.submit} disabled={isSubmitting}>
          {isSubmitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </div>
  );
}
